import React, { useEffect, useMemo, useState } from 'react';
import moment from 'moment';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import {
  createInitialProposalForm,
  optionExists,
  selectMatchingSlot,
} from '../../../utils/appointmentProposalForm';

export default function AppointmentProposalDialog({
  open,
  appointment,
  services = [],
  teamMembers = [],
  availableSlots = [],
  onClose,
  onSubmit,
  labels,
  busy = false,
  loadingServices = false,
  loadingEmployees = false,
  loadingSlots = false,
  onServiceChange,
  onEmployeeChange,
  onDateChange,
  error,
}) {
  const [form, setForm] = useState(() => createInitialProposalForm(appointment));

  useEffect(() => {
    if (open) setForm(createInitialProposalForm(appointment));
  }, [open, appointment]);

  useEffect(() => {
    if (
      !open
      || loadingEmployees
      || !form.proposed_team_member
      || optionExists(teamMembers, form.proposed_team_member)
    ) return;
    setForm((previous) => ({
      ...previous,
      proposed_team_member: '',
      proposed_start_time: '',
    }));
    onEmployeeChange?.('');
  }, [form.proposed_team_member, loadingEmployees, onEmployeeChange, open, teamMembers]);

  useEffect(() => {
    if (!open || !form.proposed_start_time || loadingSlots) return;
    const matchingSlot = selectMatchingSlot(availableSlots, form.proposed_start_time);
    setForm((previous) => (
      previous.proposed_start_time === matchingSlot
        ? previous
        : { ...previous, proposed_start_time: matchingSlot }
    ));
  }, [availableSlots, form.proposed_start_time, loadingSlots, open]);

  const deadline = useMemo(
    () => new Date(Date.now() + Number(form.expiry_hours || 0) * 3600000),
    [form.expiry_hours],
  );
  const valid = Boolean(
    form.proposed_service
      && form.proposed_team_member
      && form.proposed_date
      && form.proposed_start_time
      && Number(form.expiry_hours) >= 1
      && Number(form.expiry_hours) <= 720,
  );
  const employeeValue = (
    loadingEmployees || optionExists(teamMembers, form.proposed_team_member)
  ) ? form.proposed_team_member : '';

  const changeService = (event) => {
    const value = event.target.value;
    setForm((previous) => ({
      ...previous,
      proposed_service: value,
      proposed_start_time: '',
    }));
    onServiceChange?.(value);
  };
  const changeEmployee = (event) => {
    const value = event.target.value;
    setForm((previous) => ({
      ...previous,
      proposed_team_member: value,
      proposed_start_time: '',
    }));
    onEmployeeChange?.(value);
  };
  const changeDate = (event) => {
    const value = event.target.value;
    setForm((previous) => ({
      ...previous,
      proposed_date: value,
      proposed_start_time: '',
    }));
    onDateChange?.(value);
  };
  const change = (key) => (event) => {
    setForm((previous) => ({ ...previous, [key]: event.target.value }));
  };
  const handleSubmit = () => onSubmit({
    ...form,
    expiry_hours: Number(form.expiry_hours),
    proposed_service: Number(form.proposed_service),
    proposed_team_member: Number(form.proposed_team_member),
  });

  return (
    <Dialog
      open={open}
      onClose={busy ? undefined : onClose}
      fullWidth
      maxWidth="md"
      PaperProps={{
        sx: {
          borderRadius: 4,
          overflow: 'hidden',
          backgroundColor: '#fffdf9',
          boxShadow: '0 24px 70px rgba(79, 51, 30, 0.22)',
        },
      }}
    >
      <DialogTitle sx={{ px: { xs: 2.5, sm: 4 }, pt: 3.5, pb: 2 }}>
        <Typography component="span" variant="h5" sx={{ color: '#5e351f', fontWeight: 800, display: 'block' }}>
          {labels.title}
        </Typography>
        <Typography variant="body2" sx={{ color: '#8a6a54', mt: 0.5 }}>
          {labels.intro}
        </Typography>
      </DialogTitle>
      <Divider />
      <DialogContent sx={{ px: { xs: 2.5, sm: 4 }, py: 3 }}>
        <Box sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
          gap: 2,
        }}>
          <TextField
            select
            label={labels.service}
            value={form.proposed_service}
            onChange={changeService}
            disabled={busy || loadingServices}
            fullWidth
            sx={{ gridColumn: '1 / -1' }}
            InputProps={{
              endAdornment: loadingServices ? <CircularProgress size={18} sx={{ mr: 3 }} /> : null,
            }}
          >
            {services.map((service) => (
              <MenuItem key={service.service_id} value={String(service.service_id)}>
                <Box>
                  <Typography fontWeight={700}>{service.localized_name || service.name}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {service.formatted_duration || `${service.duration} min`} · {service.price} DA
                  </Typography>
                </Box>
              </MenuItem>
            ))}
          </TextField>
          <TextField
            select
            label={labels.employee}
            value={employeeValue}
            onChange={changeEmployee}
            disabled={busy || loadingEmployees || !form.proposed_service}
            fullWidth
            InputProps={{
              endAdornment: loadingEmployees ? <CircularProgress size={18} sx={{ mr: 3 }} /> : null,
            }}
          >
            {!loadingEmployees && teamMembers.length === 0 && (
              <MenuItem value="" disabled>{labels.noEmployees}</MenuItem>
            )}
            {teamMembers.map((member) => (
              <MenuItem key={member.team_member_id} value={String(member.team_member_id)}>
                {member.user_details?.first_name} {member.user_details?.last_name}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            type="date"
            label={labels.date}
            value={form.proposed_date}
            onChange={changeDate}
            disabled={busy}
            InputLabelProps={{ shrink: true }}
            fullWidth
          />
          <TextField
            select
            label={labels.slot}
            value={form.proposed_start_time}
            onChange={change('proposed_start_time')}
            disabled={busy || loadingSlots || !form.proposed_team_member || !form.proposed_date}
            fullWidth
            InputProps={{
              endAdornment: loadingSlots ? <CircularProgress size={18} sx={{ mr: 3 }} /> : null,
            }}
          >
            {!loadingSlots && availableSlots.length === 0 && (
              <MenuItem value="" disabled>{labels.noSlots}</MenuItem>
            )}
            {availableSlots.map((slot) => (
              <MenuItem key={slot} value={slot}>
                {moment.parseZone(slot).format('DD/MM/YYYY HH:mm')}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            type="number"
            label={labels.expiry}
            value={form.expiry_hours}
            onChange={change('expiry_hours')}
            inputProps={{ min: 1, max: 720 }}
            disabled={busy}
            fullWidth
          />
          <TextField
            label={labels.message}
            value={form.message}
            onChange={change('message')}
            multiline
            rows={3}
            disabled={busy}
            fullWidth
            sx={{ gridColumn: '1 / -1' }}
          />
        </Box>
        <Paper elevation={0} sx={{ mt: 2, p: 1.5, borderRadius: 2.5, backgroundColor: '#f7eee5' }}>
          <Typography variant="body2" sx={{ color: '#6f4a32' }}>
            {labels.deadline}: {Number.isNaN(deadline.getTime()) ? '—' : deadline.toLocaleString()}
          </Typography>
        </Paper>
        {error && <Alert severity="warning" sx={{ mt: 2 }}>{error}</Alert>}
      </DialogContent>
      <Divider />
      <DialogActions sx={{ px: { xs: 2.5, sm: 4 }, py: 2.5 }}>
        <Stack
          direction={{ xs: 'column-reverse', sm: 'row' }}
          spacing={1.25}
          sx={{ width: { xs: '100%', sm: 'auto' } }}
        >
          <Button disabled={busy} onClick={onClose}>{labels.close}</Button>
          <Button
            variant="contained"
            disabled={busy || !valid}
            onClick={handleSubmit}
            sx={{ backgroundColor: '#a45b2a', '&:hover': { backgroundColor: '#82451f' } }}
          >
            {labels.send}
          </Button>
        </Stack>
      </DialogActions>
    </Dialog>
  );
}
